import { ArrowDownIcon } from '@synergy/theme-next';
import {
    Box,
    ButtonGroup,
    ButtonGroupProps,
    ButtonProps,
    ClickAwayListener,
    Grow,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Stack,
} from '@mui/material';
import { ReactElement, ReactNode, useRef, useState } from 'react';

import { PoolButton } from '../pool-button/pool-button';

interface SplitButtonMenuItem {
    label: ReactNode;
    displayLabel?: ReactNode;
    icon?: ReactNode;
    onClick: () => void;
}

interface PoolSplitButtonProps extends ButtonGroupProps {
    menuItems: SplitButtonMenuItem[];
    leftButtonProps?: ButtonProps;
    rightButtonProps?: ButtonProps;
    preSelectedIndex?: number;
}

export const PoolSplitButton = (props: PoolSplitButtonProps): ReactElement => {
    const { menuItems, leftButtonProps, rightButtonProps, preSelectedIndex = 0, ...buttonGroupProps } = props;
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const [selectedIndex, setSelectedIndex] = useState(preSelectedIndex);

    const handleButtonClick = (): void => {
        menuItems[selectedIndex].onClick();
    };

    const handleMenuItemClick = (index: number): void => {
        setSelectedIndex(index);
        menuItems[index].onClick();
        setOpen(false);
    };

    const handleClose = (event: Event): void => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setOpen(false);
    };

    return (
        <>
            <ButtonGroup variant="contained" ref={anchorRef} {...buttonGroupProps}>
                <PoolButton startIcon={menuItems[selectedIndex].icon} onClick={handleButtonClick} {...leftButtonProps}>
                    {menuItems[selectedIndex].displayLabel ?? menuItems[selectedIndex].label}
                </PoolButton>
                <PoolButton size="small" onClick={() => setOpen(prev => !prev)} {...rightButtonProps}>
                    <ArrowDownIcon />
                </PoolButton>
            </ButtonGroup>
            {/* Portalled and above the app chrome: kept inline at zIndex 1 the
                menu was clipped by whichever scroll container held the button,
                which is how "Freies Produkt" ended up behind the toolbar
                (#2184). Opening downwards also keeps it clear of the header. */}
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom-end"
                role={undefined}
                transition={true}
                sx={theme => ({ zIndex: theme.zIndex.modal })}
            >
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList>
                                    {menuItems.map((item, index) => (
                                        <MenuItem
                                            key={index}
                                            selected={index === selectedIndex}
                                            onClick={() => handleMenuItemClick(index)}
                                        >
                                            <Stack direction="row" gap={1.5}>
                                                <Box>{item.icon}</Box>
                                                <Box>{item.label}</Box>
                                            </Stack>
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );
};
